import { DataSource } from '@angular/cdk/table';
import { Component, Input } from '@angular/core';
import { UntypedFormArray, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import * as Papa from 'papaparse';
import { BehaviorSubject, Observable } from 'rxjs';

export interface PreviewColumnConfig {
  property: string;
  header: string;
  unique: boolean;
  default: any;
  role: boolean;
  isBoolean: boolean;
}

@Component({
    selector: 'ca-csv-preview',
    templateUrl: 'csv-preview.component.html',
    styleUrls: ['csv-preview.component.scss'],
    standalone: false
})
export class CsvPreviewComponent {
  protected _file: any;
  displayedColumns: BehaviorSubject<string[]> = new BehaviorSubject([]);
  dataSource: CsvDataSource;
  rows: UntypedFormArray;
  data = new BehaviorSubject([]);
  form = new UntypedFormGroup({});

  get valid() {
    return this.form.valid;
  }
  get value() {
    return this.form.value;
  }
  @Input() config: PreviewColumnConfig[];
  @Input()
  set results(value: any[]) {
    this._results = value;
    if (value) {
      this.form.disable();
    }
  }
  get results() {
    return this._results;
  }
  @Input() error: any;
  @Input()
  set loading(loading: boolean) {
    if (loading) {
      this.form.disable();
    } else if (!this.results) {
      this.form.enable();
    }
    this._loading = loading;
  }
  get loading() {
    return this._loading;
  }

  @Input()
  set file(value) {
    if (value) {
      Papa.parse(value, {
        header: true,
        complete: this.initTable.bind(this),
        skipEmptyLines: true,
      });
    } else {
      this.initTable(null);
    }
  }

  private _loading: boolean;
  private _results: any[];

  initTable(result) {
    this.displayedColumns.next(
      this.config.map((col) => col.property).concat(['allowsMedia', 'action', 'result'])
    );

    // Filter out rows that have duplicates of unique keys
    this.config.forEach((option) => {
      const existing = new Set();
      if (option.unique) {
        result.data = result.data.filter((item) => {
          if (existing.has(item[option.property])) {
            return false;
          }
          existing.add(item[option.property]);
          return true;
        });
      }
      if (option.default) {
        result.data.forEach(
          (item) => (item[option.property] = item[option.property] || option.default)
        );
      }
      // Ensure all boolean properties are parsed.
      // If left null, seek to use default value before setting as false.
      if (option.isBoolean) {
        result.data.forEach((item) => {
          let parsedBool;
          if (!!item[option.property]) {
            try {
              parsedBool = Boolean(JSON.parse(item[option.property]));
            } catch (err) {
              parsedBool = (item[option.property]?.toLowerCase() ?? '') === 'true';
            }
          }
          item[option.property] = parsedBool ?? option.default ?? false;
        });
      }
    });

    this.rows = new UntypedFormArray(
      result.data.map((row) => {
        const fg = new UntypedFormGroup({});
        this.config.forEach((option) => {
          fg.addControl(option.property, new UntypedFormControl(row[option.property]));
        });
        return fg;
      }),
      Validators.minLength(1)
    );

    this.form.setControl('rows', this.rows);
    this.data.next(result.data);
    this.dataSource = new CsvDataSource(this.data.asObservable());
  }

  controlForCell(index: number, property: string) {
    try {
      const item: any = this.rows.controls[index];
      return item.controls[property];
    } catch (ex) {
      return new UntypedFormControl('');
    }
  }

  removeAt(idx: number) {
    this.rows.removeAt(idx);
    const items = this.data.value;
    this.data.next(items.slice(0, idx).concat(items.slice(idx + 1)));
  }

  flattenError(result: any) {
    if (result.errors) {
      const errors = result.errors;
      try {
        for (const key in errors) {
          if (Array.isArray(errors[key])) {
            return `Failed (${this.titleCase(key)}: ${errors[key][0]})`;
          }
        }
      } catch (ex) {}
    }
    return 'Import failed.';
  }

  titleCase(value: string) {
    return (
      value[0].toUpperCase() +
      value
        .replace(/([A-Z])/g, ' $1')
        .toLowerCase()
        .slice(1)
    );
  }
}

export class CsvDataSource extends DataSource<any> {
  constructor(public data: Observable<any[]>) {
    super();
  }

  connect(): Observable<any[]> {
    return this.data;
  }

  disconnect() {
    // lint
  }
}
